import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import moment from 'moment-timezone';
import { EGG_COLOUR, GDD_THRESHOLD, TRAP_CATCH_COLOUR } from '../../utils';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const SIX_DAYS_MS = 6 * ONE_DAY_MS;

const GenerationTimingsChart = ({
  dates,
  sprayData,
  trapCatchData,
  eggData,
  gddAccumulationData,
  g2TrapCatchPeakDate
}) => {
  const trapCatchSeries = trapCatchData
    .map((d) => [moment.tz(d.date, 'Pacific/Auckland').valueOf(), d.amount])
    .sort((a, b) => a[0] - b[0]);

  const eggSeries = (eggData || [])
    .map((d) => [moment.tz(d.date, 'Pacific/Auckland').valueOf(), d.amount])
    .sort((a, b) => a[0] - b[0]);

  const g2PeakValue = g2TrapCatchPeakDate
    ? moment.tz(g2TrapCatchPeakDate, 'Pacific/Auckland').valueOf()
    : null;

  const thresholdIndex = gddAccumulationData.findIndex(
    (v) => Number(v) >= GDD_THRESHOLD
  );
  const cutoff = thresholdIndex === -1 ? dates.length : thresholdIndex + 1;
  const gddAccumulationSeries = dates
    .slice(0, cutoff)
    .map((seconds, i) => [seconds * 1000, Number(gddAccumulationData[i])]);

  const sortedSprays = (sprayData || [])
    .filter((sd) => sd.date)
    .sort(
      (a, b) =>
        moment.tz(a.date, 'Pacific/Auckland').valueOf() -
        moment.tz(b.date, 'Pacific/Auckland').valueOf()
    );

  const sprayBands = sortedSprays.map((spray, i) => {
    const sprayDate = moment.tz(spray.date, 'Pacific/Auckland').valueOf();
    const lookbackFrom = sprayDate - SIX_DAYS_MS;
    const highEffectiveTo = sprayDate + SIX_DAYS_MS;
    const forwardTo = sprayDate + 2 * SIX_DAYS_MS;
    return {
      sprayDate,
      from: lookbackFrom,
      to: forwardTo,
      number: i + 1,
      spray,
      windows: {
        lookbackFrom,
        sprayDate,
        highEffectiveTo,
        forwardTo
      }
    };
  });

  const sprayPlotBands = sprayBands.flatMap(({ number, windows }) => {
    const { lookbackFrom, sprayDate, highEffectiveTo, forwardTo } = windows;
    return [
      {
        from: lookbackFrom,
        to: sprayDate,
        color: 'rgba(255, 165, 0, 0.25)',
        zIndex: 3,
        label: {
          text: `#${number}`,
          style: {
            color: '#666',
            fontSize: '12px',
            fontWeight: 'bold'
          },
          align: 'center',
          verticalAlign: 'top',
          y: 20
        }
      },
      {
        from: sprayDate,
        to: highEffectiveTo,
        color: 'rgba(128, 0, 128, 0.25)'
      },
      {
        from: highEffectiveTo,
        to: forwardTo,
        color: 'rgba(0, 128, 0, 0.25)'
      }
    ];
  });

  const sprayPlotLines = sprayBands.map(({ sprayDate }) => ({
    value: sprayDate,
    color: 'red',
    width: 2,
    zIndex: 5
  }));

  const options = {
    chart: {
      type: 'line'
    },
    credits: {
      enabled: false
    },
    time: {
      timezone: 'Pacific/Auckland'
    },
    title: {
      text: 'Generation Timings & Spray Dates'
    },
    tooltip: {
      shared: true,
      formatter: function () {
        const x = this.x;
        const chart = this.points?.[0]?.series.chart;
        const formatDate = (format, value) =>
          chart
            ? chart.time.dateFormat(format, value)
            : Highcharts.dateFormat(format, value);
        let html = this.points.reduce(
          (s, point) => {
            const dp = point.series.options.custom?.decimals;
            const value =
              dp != null ? Highcharts.numberFormat(point.y, dp) : point.y;
            return (
              s +
              `<br/><span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${value}</b>`
            );
          },
          `<span style="font-size: 10px">${formatDate('%A, %b %e, %Y', x)}</span>`
        );

        const activeSprays = sprayBands.filter(
          (band) => x >= band.from && x <= band.to
        );
        if (activeSprays.length) {
          activeSprays.forEach((band) => {
            html += `<br/><span style="color:green">\u25CF</span> Spray #${band.number} (${band.spray.efficacy_days} days)`;
          });
        }

        return html;
      }
    },
    xAxis: {
      type: 'datetime',
      tickInterval: 2 * 7 * 24 * 3600 * 1000,
      gridLineWidth: 1,
      labels: {
        format: '{value:%e-%b}'
      },
      plotBands: sprayPlotBands,
      plotLines: [
        ...sprayPlotLines,
        ...(g2PeakValue
          ? [
              {
                value: g2PeakValue,
                color: TRAP_CATCH_COLOUR,
                width: 2,
                dashStyle: 'Dash',
                zIndex: 5,
                label: {
                  text: 'G2',
                  rotation: 0,
                  verticalAlign: 'top',
                  y: 20,
                  x: -28,
                  style: {
                    color: TRAP_CATCH_COLOUR,
                    fontWeight: 'bold',
                    fontSize: '15px'
                  }
                }
              }
            ]
          : [])
      ]
    },
    yAxis: [
      {
        title: {
          text: 'G2 Trap Catch & Egg Infestation (%)'
        }
      },
      {
        title: {
          text: "G3 GDD's"
        },
        opposite: true
      }
    ],
    legend: {
      enabled: true
    },
    plotOptions: {
      line: {
        marker: {
          enabled: false
        }
      }
    },
    series: [
      {
        name: 'G2 Trap Catch',
        data: trapCatchSeries,
        color: TRAP_CATCH_COLOUR,
        yAxis: 0
      },
      {
        name: 'Egg Infestation (%)',
        data: eggSeries,
        color: EGG_COLOUR,
        custom: {
          decimals: 2
        },
        yAxis: 0
      },
      {
        name: "G3 GDD's",
        data: gddAccumulationSeries,
        color: 'black',
        custom: {
          decimals: 2
        },
        yAxis: 1
      }
    ]
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export default GenerationTimingsChart;

